import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * WineSearchSelect — debounced combobox for selecting a wine.
 *
 * - Searches the wine catalogue as the user types (300 ms debounce)
 * - Displays colour, name, vintage, and producer in the dropdown
 * - "Add new wine…" option opens WineCreateModal with full master-data form
 * - Compatible with React Hook Form via <Controller>
 */
import { useState, useEffect, useRef } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { Search, Plus, X, Loader2, ChevronDown } from 'lucide-react';
import { winesApi, producersApi } from '../../lib/api';
import { Modal } from '../ui/Modal';
import { FormField, Input, Select, Textarea } from '../ui/FormField';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { cn } from '../../lib/utils';
// ── Colour dot ────────────────────────────────────────────────────────────────
const COLOUR_DOT_CLASS = {
    red: 'bg-red-600',
    white: 'bg-amber-100 border border-amber-300',
    rose: 'bg-pink-400',
    orange: 'bg-orange-400',
    sparkling: 'bg-slate-300 border border-slate-400',
    sweet: 'bg-amber-300',
    fortified: 'bg-amber-800',
    other: 'bg-gray-400',
};
const COLOUR_OPTIONS = [
    { value: 'red', label: 'Red' },
    { value: 'white', label: 'White' },
    { value: 'rose', label: 'Rosé' },
    { value: 'orange', label: 'Orange' },
    { value: 'sparkling', label: 'Sparkling' },
    { value: 'sweet', label: 'Sweet / Dessert' },
    { value: 'fortified', label: 'Fortified / Port' },
    { value: 'other', label: 'Other' },
];
const BOTTLE_FORMAT_OPTIONS = [
    { value: 'standard', label: 'Standard (75 cl)' },
    { value: 'half', label: 'Half bottle (37.5 cl)' },
    { value: 'magnum', label: 'Magnum (150 cl)' },
    { value: 'double_magnum', label: 'Double Magnum (300 cl)' },
    { value: 'jeroboam', label: 'Jeroboam (300 cl)' },
    { value: 'rehoboam', label: 'Rehoboam (450 cl)' },
    { value: 'imperial', label: 'Imperial (600 cl)' },
];
function ColourDot({ colour }) {
    return (_jsx("span", { className: cn('inline-block w-2.5 h-2.5 rounded-full flex-shrink-0', COLOUR_DOT_CLASS[colour] ?? 'bg-gray-400'), title: colour }));
}
function wineLabel(w) {
    const parts = [w.name];
    if (w.producerName)
        parts.push(`— ${w.producerName}`);
    return parts.join(' ');
}
function ProducerField({ value, onChange, error }) {
    const [query, setQuery] = useState(value.producerName ?? '');
    const [results, setResults] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const containerRef = useRef(null);
    useEffect(() => {
        if (query.length < 2) {
            setResults([]);
            return;
        }
        const t = setTimeout(async () => {
            setLoading(true);
            try {
                const data = await producersApi.list(query);
                setResults(data.producers.slice(0, 8));
            }
            catch { /* ignore network errors in typeahead */ }
            finally {
                setLoading(false);
            }
        }, 300);
        return () => clearTimeout(t);
    }, [query]);
    useEffect(() => {
        function onMouseDown(e) {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        }
        document.addEventListener('mousedown', onMouseDown);
        return () => document.removeEventListener('mousedown', onMouseDown);
    }, []);
    const isNewProducer = !value.producerId && !!query;
    return (_jsxs("div", { className: "space-y-2", children: [_jsxs("div", { ref: containerRef, className: "relative", children: [_jsxs("div", { className: "relative", children: [_jsx(Search, { className: "absolute left-3 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" }), _jsx("input", { type: "text", value: query, placeholder: "Search or enter producer name\u2026", autoComplete: "off", className: cn('input pl-9 pr-8', error && 'ring-red-500 focus:ring-red-500'), onChange: (e) => {
                                    const v = e.target.value;
                                    setQuery(v);
                                    onChange({ producerName: v, producerCountryCode: value.producerCountryCode });
                                    setIsOpen(true);
                                }, onFocus: () => { if (query.length >= 2)
                                    setIsOpen(true); } }), loading ? (_jsx(Loader2, { className: "absolute right-3 top-2.5 h-4 w-4 animate-spin text-gray-400" })) : value.producerId ? (_jsx("button", { type: "button", onClick: () => { setQuery(''); onChange({}); }, className: "absolute right-2.5 top-2 p-0.5 text-gray-400 hover:text-gray-600", children: _jsx(X, { className: "h-4 w-4" }) })) : null] }), isOpen && (results.length > 0 || query.length >= 2) && (_jsxs("div", { className: "absolute z-30 top-full mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-200 max-h-52 overflow-y-auto", children: [results.map((p) => (_jsxs("button", { type: "button", className: "w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2", onMouseDown: (e) => {
                                    e.preventDefault();
                                    setQuery(p.name);
                                    onChange({ producerId: p.id, producerName: p.name, producerCountryCode: p.countryCode ?? undefined });
                                    setIsOpen(false);
                                }, children: [_jsx("span", { className: "font-medium text-gray-900", children: p.name }), p.countryCode && (_jsx("span", { className: "ml-auto text-xs text-gray-400 uppercase font-mono", children: p.countryCode }))] }, p.id))), query.length >= 2 && (_jsxs("button", { type: "button", className: "w-full text-left px-3 py-2 text-sm text-emerald-700 hover:bg-emerald-50 font-medium border-t border-gray-100 flex items-center gap-1.5", onMouseDown: (e) => {
                                    e.preventDefault();
                                    onChange({ producerName: query, producerCountryCode: value.producerCountryCode });
                                    setIsOpen(false);
                                }, children: [_jsx(Plus, { className: "h-3.5 w-3.5" }), "Create new producer \"", query, "\""] }))] }))] }), isNewProducer && (_jsxs("div", { className: "flex items-center gap-2 pl-1", children: [_jsx("span", { className: "text-xs text-gray-500 w-16 flex-shrink-0", children: "Country:" }), _jsx("input", { type: "text", value: value.producerCountryCode ?? '', onChange: (e) => onChange({ ...value, producerCountryCode: e.target.value.toUpperCase().slice(0, 2) }), placeholder: "FR", maxLength: 2, className: "input w-16 text-center uppercase text-sm font-mono" }), _jsx("span", { className: "text-xs text-gray-400", children: "2-letter ISO code" })] })), error && _jsx("p", { className: "form-error", children: error })] }));
}
function WineCreateModal({ isOpen, initialQuery, onClose, onCreated }) {
    const [producer, setProducer] = useState({});
    const [producerError, setProducerError] = useState();
    const [submitting, setSubmitting] = useState(false);
    const [serverError, setServerError] = useState();
    const { register, control, handleSubmit, reset, formState: { errors }, } = useForm({
        defaultValues: {
            name: '',
            colour: '',
            countryCode: '',
            regionName: '',
            regionCountryCode: '',
            classification: '',
            alcoholAbv: '',
            defaultBottleFormat: 'standard',
            drinkFrom: '',
            drinkTo: '',
            peakStart: '',
            peakEnd: '',
            notes: '',
            maturityNotes: '',
            grapeVarieties: [],
        },
    });
    const { fields: grapeFields, append: appendGrape, remove: removeGrape } = useFieldArray({
        control,
        name: 'grapeVarieties',
    });
    // Reset when the modal opens with a new search query
    useEffect(() => {
        if (isOpen) {
            reset({
                name: initialQuery,
                colour: '',
                countryCode: '',
                regionName: '',
                regionCountryCode: '',
                classification: '',
                alcoholAbv: '',
                defaultBottleFormat: 'standard',
                drinkFrom: '',
                drinkTo: '',
                peakStart: '',
                peakEnd: '',
                notes: '',
                maturityNotes: '',
                grapeVarieties: [],
            });
            setProducer({});
            setProducerError(undefined);
            setServerError(undefined);
        }
    }, [isOpen, initialQuery, reset]);
    async function onSubmit(values) {
        if (!producer.producerId && !producer.producerName) {
            setProducerError('Producer is required');
            return;
        }
        setProducerError(undefined);
        setSubmitting(true);
        setServerError(undefined);
        try {
            const wine = await winesApi.create({
                name: values.name,
                colour: values.colour,
                ...(producer.producerId
                    ? { producerId: producer.producerId }
                    : { producerName: producer.producerName, producerCountryCode: producer.producerCountryCode }),
                countryCode: values.countryCode || undefined,
                regionName: values.regionName || undefined,
                regionCountryCode: values.regionCountryCode || undefined,
                classification: values.classification || undefined,
                alcoholAbv: values.alcoholAbv ? parseFloat(values.alcoholAbv) : undefined,
                defaultBottleFormat: values.defaultBottleFormat || undefined,
                drinkFrom: values.drinkFrom ? parseInt(values.drinkFrom, 10) : undefined,
                drinkTo: values.drinkTo ? parseInt(values.drinkTo, 10) : undefined,
                peakStart: values.peakStart ? parseInt(values.peakStart, 10) : undefined,
                peakEnd: values.peakEnd ? parseInt(values.peakEnd, 10) : undefined,
                notes: values.notes || undefined,
                maturityNotes: values.maturityNotes || undefined,
                grapeVarieties: values.grapeVarieties
                    .filter((g) => g.varietyName.trim())
                    .map((g) => ({
                    varietyName: g.varietyName,
                    percentage: g.percentage ? parseFloat(g.percentage) : undefined,
                })),
            });
            // Map WineDetail → WineListItem for the combobox selection state
            const listItem = {
                id: wine.id,
                name: wine.name,
                colour: wine.colour,
                producerName: wine.producerName,
                regionName: wine.regionName,
                bottleCount: 0,
                drinkFrom: wine.drinkFrom,
                drinkTo: wine.drinkTo,
                peakStart: wine.peakStart,
                peakEnd: wine.peakEnd,
                effectiveMaturityState: wine.effectiveMaturityState,
            };
            onCreated(listItem);
        }
        catch (e) {
            setServerError(e instanceof Error ? e.message : 'Failed to create wine');
        }
        finally {
            setSubmitting(false);
        }
    }
    return (_jsxs(Modal, { isOpen: isOpen, onClose: onClose, title: "Add new wine", size: "lg", footer: _jsxs(_Fragment, { children: [_jsx("button", { type: "button", onClick: onClose, className: "btn-secondary", children: "Cancel" }), _jsx("button", { type: "submit", form: "wine-create-form", disabled: submitting, className: "btn-primary", children: submitting ? _jsx(LoadingSpinner, { size: "sm" }) : 'Save wine' })] }), children: [serverError && (_jsx("div", { className: "mb-4 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700", children: serverError })), _jsxs("form", { id: "wine-create-form", onSubmit: handleSubmit(onSubmit), className: "space-y-6", children: [_jsxs("section", { className: "space-y-3", children: [_jsx("h3", { className: "text-xs font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-100 pb-1", children: "Identity" }), _jsx(FormField, { label: "Wine name / cuv\u00E9e", required: true, id: "wc-name", error: errors.name?.message, children: _jsx(Input, { id: "wc-name", placeholder: "e.g. Ch\u00E2teau L\u00E9oville-Barton", error: Boolean(errors.name), ...register('name', { required: 'Wine name is required' }) }) }), _jsx("div", { className: "grid grid-cols-2 gap-3", children: _jsx(FormField, { label: "Colour / type", required: true, id: "wc-colour", error: errors.colour?.message, children: _jsx(Select, { id: "wc-colour", placeholder: "Select colour", options: COLOUR_OPTIONS, error: Boolean(errors.colour), ...register('colour', { required: 'Colour is required' }) }) }) })] }), _jsxs("section", { className: "space-y-3", children: [_jsx("h3", { className: "text-xs font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-100 pb-1", children: "Producer" }), _jsx(FormField, { label: "Producer / winery", required: true, error: producerError, children: _jsx(ProducerField, { value: producer, onChange: setProducer, error: producerError }) })] }), _jsxs("section", { className: "space-y-3", children: [_jsx("h3", { className: "text-xs font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-100 pb-1", children: "Provenance" }), _jsxs("div", { className: "grid grid-cols-2 gap-3", children: [_jsx(FormField, { label: "Region / appellation", id: "wc-region", children: _jsx(Input, { id: "wc-region", placeholder: "e.g. Saint-Julien", ...register('regionName') }) }), _jsx(FormField, { label: "Region country", id: "wc-region-cc", hint: "2-letter ISO", children: _jsx(Input, { id: "wc-region-cc", placeholder: "FR", maxLength: 2, className: "uppercase", ...register('regionCountryCode', {
                                                setValueAs: (v) => v.toUpperCase(),
                                            }) }) })] }), _jsxs("div", { className: "grid grid-cols-2 gap-3", children: [_jsx(FormField, { label: "Classification / hierarchy", id: "wc-class", children: _jsx(Input, { id: "wc-class", placeholder: "e.g. Premier Grand Cru Class\u00E9", ...register('classification') }) }), _jsx(FormField, { label: "ABV (%)", id: "wc-abv", children: _jsx(Input, { id: "wc-abv", type: "number", min: 0, max: 100, step: 0.1, placeholder: "13.5", ...register('alcoholAbv') }) })] }), _jsx(FormField, { label: "Default bottle format", id: "wc-format", children: _jsx(Select, { id: "wc-format", options: BOTTLE_FORMAT_OPTIONS, ...register('defaultBottleFormat') }) })] }), _jsxs("section", { className: "space-y-3", children: [_jsxs("div", { className: "flex items-center justify-between border-b border-gray-100 pb-1", children: [_jsx("h3", { className: "text-xs font-semibold uppercase tracking-wide text-gray-500", children: "Grape varieties" }), _jsxs("button", { type: "button", onClick: () => appendGrape({ varietyName: '', percentage: '' }), className: "btn-ghost btn-sm text-xs gap-1", children: [_jsx(Plus, { className: "h-3 w-3" }), "Add variety"] })] }), grapeFields.length === 0 && (_jsx("p", { className: "text-xs text-gray-400 italic", children: "Optional \u2014 add any grape varieties" })), grapeFields.map((field, i) => (_jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Input, { placeholder: "Variety (e.g. Cabernet Sauvignon)", className: "flex-1", ...register(`grapeVarieties.${i}.varietyName`) }), _jsx(Input, { type: "number", placeholder: "%", min: 0, max: 100, className: "w-20 flex-shrink-0", ...register(`grapeVarieties.${i}.percentage`) }), _jsx("button", { type: "button", onClick: () => removeGrape(i), className: "p-1 text-gray-400 hover:text-red-500 flex-shrink-0", children: _jsx(X, { className: "h-4 w-4" }) })] }, field.id)))] }), _jsxs("section", { className: "space-y-3", children: [_jsx("h3", { className: "text-xs font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-100 pb-1", children: "Drinking window" }), _jsxs("div", { className: "grid grid-cols-2 gap-3", children: [_jsx(FormField, { label: "Drink from (year)", id: "wc-df", children: _jsx(Input, { id: "wc-df", type: "number", min: 2000, max: 2100, placeholder: "2024", ...register('drinkFrom') }) }), _jsx(FormField, { label: "Drink to (year)", id: "wc-dt", children: _jsx(Input, { id: "wc-dt", type: "number", min: 2000, max: 2100, placeholder: "2040", ...register('drinkTo') }) }), _jsx(FormField, { label: "Peak start (year)", id: "wc-ps", children: _jsx(Input, { id: "wc-ps", type: "number", min: 2000, max: 2100, placeholder: "2028", ...register('peakStart') }) }), _jsx(FormField, { label: "Peak end (year)", id: "wc-pe", children: _jsx(Input, { id: "wc-pe", type: "number", min: 2000, max: 2100, placeholder: "2035", ...register('peakEnd') }) })] })] }), _jsxs("section", { className: "space-y-3", children: [_jsx("h3", { className: "text-xs font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-100 pb-1", children: "Notes" }), _jsx(FormField, { label: "General notes", id: "wc-notes", children: _jsx(Textarea, { id: "wc-notes", rows: 2, placeholder: "Tasting profile, style, character\u2026", ...register('notes') }) }), _jsx(FormField, { label: "Maturity & ageing notes", id: "wc-maturity", children: _jsx(Textarea, { id: "wc-maturity", rows: 2, placeholder: "Expected plateau, decline curve, optimal ageing conditions\u2026", ...register('maturityNotes') }) })] })] })] }));
}
export function WineSearchSelect({ value, onChange, onWineSelect, error, disabled = false, placeholder = 'Search wines…', }) {
    const [query, setQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchError, setSearchError] = useState(null);
    const [selectedWine, setSelectedWine] = useState(null);
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [createQuery, setCreateQuery] = useState('');
    const containerRef = useRef(null);
    const inputRef = useRef(null);
    // Load selected wine label when the component receives a pre-set value
    useEffect(() => {
        if (!value) {
            setSelectedWine(null);
            return;
        }
        if (selectedWine?.id === value)
            return;
        winesApi.get(value)
            .then((w) => setSelectedWine({
            id: w.id, name: w.name, colour: w.colour,
            producerName: w.producerName, regionName: w.regionName, bottleCount: 0,
            drinkFrom: w.drinkFrom, drinkTo: w.drinkTo,
            peakStart: w.peakStart, peakEnd: w.peakEnd,
            effectiveMaturityState: w.effectiveMaturityState,
        }))
            .catch(() => { });
    }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
    // Debounced search
    useEffect(() => {
        if (!isOpen || query.length < 1) {
            setResults([]);
            setSearchError(null);
            return;
        }
        const t = setTimeout(async () => {
            setLoading(true);
            setSearchError(null);
            try {
                const data = await winesApi.list({ q: query, pageSize: 10 });
                setResults(data.wines);
            }
            catch (e) {
                setSearchError(e instanceof Error ? e.message : 'Search failed');
                setResults([]);
            }
            finally {
                setLoading(false);
            }
        }, 300);
        return () => clearTimeout(t);
    }, [query, isOpen]);
    // Close on outside click
    useEffect(() => {
        function onMouseDown(e) {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        }
        document.addEventListener('mousedown', onMouseDown);
        return () => document.removeEventListener('mousedown', onMouseDown);
    }, []);
    function selectWine(wine) {
        setSelectedWine(wine);
        onChange(wine.id);
        onWineSelect?.(wine);
        setIsOpen(false);
        setQuery('');
    }
    function clearSelection() {
        setSelectedWine(null);
        onChange('');
        setQuery('');
        setTimeout(() => inputRef.current?.focus(), 0);
    }
    function openCreateModal() {
        setCreateQuery(query);
        setIsCreateOpen(true);
        setIsOpen(false);
    }
    function handleCreated(wine) {
        selectWine(wine);
        setIsCreateOpen(false);
    }
    return (_jsxs(_Fragment, { children: [_jsxs("div", { ref: containerRef, className: "relative", children: [selectedWine ? (
                    /* Selected state — show chip */
                    _jsxs("div", { className: cn('input flex items-center gap-2 cursor-default', error && 'ring-red-500'), children: [_jsx(ColourDot, { colour: selectedWine.colour }), _jsx("span", { className: "flex-1 text-sm text-gray-900 truncate", children: wineLabel(selectedWine) }), !disabled && (_jsx("button", { type: "button", onClick: clearSelection, className: "ml-1 p-0.5 text-gray-400 hover:text-gray-600 flex-shrink-0", "aria-label": "Clear selection", children: _jsx(X, { className: "h-4 w-4" }) }))] })) : (
                    /* Search state — input with icon */
                    _jsxs("div", { className: "relative", children: [_jsx(Search, { className: "absolute left-3 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" }), _jsx("input", { ref: inputRef, type: "text", value: query, disabled: disabled, placeholder: placeholder, autoComplete: "off", className: cn('input pl-9 pr-8', error && 'ring-red-500 focus:ring-red-500'), onChange: (e) => { setQuery(e.target.value); setIsOpen(true); }, onFocus: () => setIsOpen(true), onKeyDown: (e) => { if (e.key === 'Escape')
                                    setIsOpen(false); } }), loading
                                ? _jsx(Loader2, { className: "absolute right-3 top-2.5 h-4 w-4 animate-spin text-gray-400" })
                                : _jsx(ChevronDown, { className: "absolute right-3 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" })] })), isOpen && !selectedWine && (_jsxs("div", { className: "absolute z-20 top-full mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden max-h-72 overflow-y-auto", children: [results.length === 0 && query.length === 0 && (_jsx("p", { className: "px-3 py-3 text-sm text-gray-400", children: "Start typing to search wines\u2026" })), searchError && (_jsxs("p", { className: "px-3 py-3 text-sm text-red-600", children: ["Error: ", searchError] })), !searchError && results.length === 0 && query.length > 0 && !loading && (_jsxs("p", { className: "px-3 py-3 text-sm text-gray-500", children: ["No wines found for \"", query, "\""] })), results.map((wine) => (_jsxs("button", { type: "button", className: "w-full text-left px-3 py-2.5 hover:bg-gray-50 flex items-center gap-2.5 border-b border-gray-100 last:border-0", onMouseDown: (e) => { e.preventDefault(); selectWine(wine); }, children: [_jsx(ColourDot, { colour: wine.colour }), _jsxs("div", { className: "flex-1 min-w-0", children: [_jsx("div", { className: "flex items-baseline gap-1.5", children: _jsx("span", { className: "text-sm font-medium text-gray-900 truncate", children: wine.name }) }), _jsxs("p", { className: "text-xs text-gray-500 truncate", children: [wine.producerName, wine.regionName ? ` · ${wine.regionName}` : ''] })] }), wine.effectiveMaturityState === 'at_peak' && (_jsx("span", { className: "flex-shrink-0 text-xs bg-emerald-100 text-emerald-700 rounded px-1.5 py-0.5 font-medium", children: "Peak" }))] }, wine.id))), _jsxs("button", { type: "button", className: "w-full text-left px-3 py-2.5 text-sm text-emerald-700 hover:bg-emerald-50 font-medium flex items-center gap-2 border-t border-gray-100", onMouseDown: (e) => { e.preventDefault(); openCreateModal(); }, children: [_jsx(Plus, { className: "h-4 w-4 flex-shrink-0" }), query ? `Add new wine "${query}"` : 'Add new wine…'] })] }))] }), error && _jsx("p", { className: "form-error mt-1", children: error }), _jsx(WineCreateModal, { isOpen: isCreateOpen, initialQuery: createQuery, onClose: () => setIsCreateOpen(false), onCreated: handleCreated })] }));
}
